import { useCallback, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import cc from 'classcat';
import dayjs from 'dayjs';
import { sortByOrder } from 'utils/collection-manipulation/array-sort-by';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { mountPoint } from 'utils/generic/route-manipulation';
import { isMobile } from 'utils/is-mobile';
import RidGenerator from 'utils/swarm/rid-generator';
import { MenuItem } from 'interfaces/generic';
import { EAdvancedEventsLayouts } from 'interfaces/sportsbook';
import { Region } from 'interfaces/sportsbook-data-levels';
import { executeCommand, unsubscribeCommand } from 'services/get-data-level';
import { AdvancedSportsMenu } from 'newelements/Sportsbook/layouts/advanced/__partials/AdvancedMainContent/__partials/AdvancedEventsLayout/__partials/AdvancedSportsMenu';
import { AdvancedGameCardsWithLoadMoreWrapper } from 'newelements/Sportsbook/layouts/advanced/__partials/AdvancedMainContent/__partials/AdvancedEventsLayout/AdvancedGameCardsWithLoadMoreWrapper';
import { EGroupGamesBy } from 'newelements/Sportsbook/layouts/advanced/__partials/AdvancedMainContent/__partials/AdvancedEventsLayout/AdvancedMatchCardsDesktop';
import { AdvancedShowAllEventsUnderlined } from 'newelements/Sportsbook/layouts/advanced/__partials/AdvancedMainContent/__partials/AdvancedShowAllEventsUnderlined';
import { ADVANCED_SKELETON_COUNTS } from 'newelements/Sportsbook/layouts/advanced/constants/skeleton-counts';
import { getGameWhereByLayoutType } from 'newelements/Sportsbook/layouts/advanced/helpers/getGameWhereByLayoutType';
import { NoItem } from 'components/Shared/NoItem';
import { AdvancedGameCardsWithMarketsFilters } from 'newcomponents/Shared/sportsbook/AdvancedMain/AdvancedGameCardsWithMarketsFilters/AdvancedGameCardsWithMarketsFilters';
import { Skeleton } from 'newcomponents/UI/Skeleton';
import { useSportsbookConfigs } from 'hooks/sportsbook/useSportsbookConfigs';
import { useMounted } from 'hooks/useMounted';
import { useSearchParams } from 'hooks/useSearchParams';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import {
  setAdvancedEventsLayoutGameIds,
  setAdvancedSportsForMarketsTypes,
  setSportsData as setMainSportMenuData,
  setSportsbookSelectedSport
} from 'store/actions/new-sportsbook';
import { getEventIds } from 'store/selectors/betslip';
import {
  getAdvancedEventsLayoutGameIds,
  getSportsbookSelectedSport
} from 'store/selectors/new-sportsbook';
import { getStreamData } from 'store/selectors/sport-data';
import './index.less';

type TAdvancedEventsLayoutProps = {
  layoutType: EAdvancedEventsLayouts;
};

const GAMES_INITIAL_SHOW_COUNT = 3;

export const AdvancedEventsLayout = ({
  layoutType
}: TAdvancedEventsLayoutProps) => {
  const configs = useSportsbookConfigs();

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { mounted } = useMounted();
  const history = useHistory();
  const { layoutType: selectedLayoutInQuery } = useSearchParams();
  const routeParams = useSbRouteMatch(
    `${mountPoint(
      window.getPathname()
    )}${`/:sport?/:region?/:competition?/:game?`}`
  );

  const sportInRoute = routeParams?.params?.sport;

  const selectedEventIds = useSelector(getEventIds).map(String);
  const streamData = useSelector(getStreamData);
  const gameIds = useSelector(getAdvancedEventsLayoutGameIds)[layoutType] || {};
  const mainSportsMenuSelectedSport = useSelector(getSportsbookSelectedSport);

  const [internalSelectedSport, setInternalSelectedSport] = useState<
    MenuItem | null | undefined
  >();

  const selectedSport = mainSportsMenuSelectedSport || internalSelectedSport;

  const noGamesAvailableForSelectedSport =
    !!selectedSport?.alias && gameIds[selectedSport.alias]?.length === 0;

  const noOverallData = internalSelectedSport === null;

  const isShowAllOrSportSelected =
    !!selectedLayoutInQuery || (!!sportInRoute && !routeParams?.params?.game);

  const gameIdsToShow =
    selectedSport?.alias && gameIds[selectedSport.alias]
      ? gameIds[selectedSport.alias]?.slice(
          0,
          isShowAllOrSportSelected ? undefined : GAMES_INITIAL_SHOW_COUNT
        )
      : null;

  const shouldShowAllEventsButton =
    selectedSport &&
    gameIds[selectedSport.alias]?.length > GAMES_INITIAL_SHOW_COUNT &&
    !noGamesAvailableForSelectedSport &&
    !isShowAllOrSportSelected;

  const onSelectedSportChange = useCallback(
    (selectedSport: MenuItem | null) => {
      setInternalSelectedSport(selectedSport);

      if (selectedSport) {
        dispatch(setAdvancedSportsForMarketsTypes(selectedSport.alias));
      }
    },
    []
  );

  const onShowAllClick = () => {
    if (selectedSport) {
      const redirectPath = `${mountPoint(window.getPathname())}/${
        selectedSport.alias
      }?layoutType=${layoutType}`;

      history.push(redirectPath);
      dispatch(setSportsbookSelectedSport(selectedSport));
      dispatch(
        setMainSportMenuData({
          [configs.type]: null
        })
      );
    }
  };

  const showTitle =
    (selectedSport && gameIds[selectedSport.alias]?.length) ||
    selectedSport === null;

  useLayoutEffect(() => {
    if (!selectedSport?.alias) {
      return;
    }

    const rId = RidGenerator.gForSubscribe(
      `ADVANCED_GET_${layoutType.toUpperCase()}_${selectedSport.alias}_GAME_IDS`
    );

    const what = {
      competition: ['order'],
      region: ['order'],
      game: ['id', 'start_ts']
    };

    if (layoutType === EAdvancedEventsLayouts.popular) {
      what.game = [...what.game, 'favorite_order'];
    }

    const where = {
      sport: { alias: selectedSport.alias },
      game: getGameWhereByLayoutType({ layoutType })
    };

    const onResponse = (data: { region: Record<string, Region> }) => {
      if (data && data.region && mounted.current) {
        const gamesArr: { id: number; start_ts: number }[] = [];
        sortByOrder(Object.values(data.region)).forEach(region => {
          region.competition &&
            sortByOrder(Object.values(region.competition)).forEach(comp => {
              comp.game &&
                sortByOrder(Object.values(comp.game), 'start_ts').forEach(
                  game => {
                    gamesArr.push(game);
                  }
                );
            });
        });

        const sortedAndFilteredGameIds = (
          layoutType === EAdvancedEventsLayouts.popular
            ? sortByOrder(gamesArr, 'favorite_order')
            : layoutType === EAdvancedEventsLayouts.upcoming
            ? sortByOrder(gamesArr, 'start_ts')
            : gamesArr
        )
          .filter(game =>
            SpringConfigs.MOCKED_DATA
              ? true
              : !!game.start_ts && game.start_ts > dayjs().startOf('day').unix()
          )
          .map(game => game.id);

        dispatch(
          setAdvancedEventsLayoutGameIds({
            data: {
              [selectedSport.alias]: sortedAndFilteredGameIds
            },
            key: layoutType
          })
        );
      }
    };

    executeCommand(rId, what, where, onResponse, true, onResponse);

    return () => {
      unsubscribeCommand(rId);
    };
  }, [selectedSport?.alias]);

  if (!!mainSportsMenuSelectedSport && noGamesAvailableForSelectedSport) {
    return null;
  }

  return (
    <div
      className={cc([
        'advancedEventsLayout',
        {
          'advancedEventsLayout--withLoadMoreBtn':
            selectedLayoutInQuery === EAdvancedEventsLayouts.upcoming &&
            isMobile()
        }
      ])}
    >
      <div className="advancedEventsLayout__title">
        <span className="advancedEventsLayout__title__wrapper">
          {showTitle ? (
            <>
              {layoutType === EAdvancedEventsLayouts.live && (
                <span className="advancedEventsLayout__title__liveDot" />
              )}
              {t(`sportsbook.${EAdvancedEventsLayouts[layoutType]}`)}
            </>
          ) : (
            <Skeleton
              title={{ style: { width: 150, margin: 0, height: 24 } }}
              paragraph={false}
              active
            />
          )}
        </span>
        {shouldShowAllEventsButton && isMobile() && (
          <span
            className="advancedEventsLayout__title__seeAll"
            onClick={onShowAllClick}
          >
            {t('sportsbook.seeAll')}
          </span>
        )}
      </div>
      {!isShowAllOrSportSelected && !noOverallData && (
        <AdvancedSportsMenu
          layoutType={layoutType}
          selectedSport={selectedSport}
          setSelectedSport={onSelectedSportChange}
        />
      )}
      {noGamesAvailableForSelectedSport || noOverallData ? (
        <NoItem
          className="advancedNoGames"
          size="medium"
          title={t('sportsbook.noGame')}
          text={t('sportsbook.noGameText')}
        />
      ) : isShowAllOrSportSelected &&
        layoutType === EAdvancedEventsLayouts.upcoming ? (
        <AdvancedGameCardsWithLoadMoreWrapper
          gameIds={gameIdsToShow}
          selectedSport={selectedSport}
          layoutType={layoutType}
          selectedEventIds={selectedEventIds}
          streamData={streamData}
          skeletonCount={15}
        />
      ) : (
        <AdvancedGameCardsWithMarketsFilters
          marketsCountsReduxKey={layoutType}
          gameIds={gameIdsToShow}
          selectedSport={selectedSport}
          layoutType={layoutType}
          selectedEventIds={selectedEventIds}
          streamData={streamData}
          skeletonCount={
            isShowAllOrSportSelected
              ? ADVANCED_SKELETON_COUNTS._15
              : ADVANCED_SKELETON_COUNTS._3
          }
          {...(selectedLayoutInQuery === EAdvancedEventsLayouts.live && {
            groupGamesBy: EGroupGamesBy.league
          })}
        />
      )}
      {((shouldShowAllEventsButton && !isMobile()) || gameIdsToShow === null) &&
        !noOverallData && (
          <AdvancedShowAllEventsUnderlined
            onClick={onShowAllClick}
            showSkeleton={gameIdsToShow === null}
          />
        )}
    </div>
  );
};
